import { Link } from "react-router-dom";
import { ArrowRight, Music, Home as HomeIcon, Briefcase, PartyPopper, Gift, Flower2, Package } from "lucide-react";
import { SERVICES, PACKAGES } from "@/data/catalog";
import { CHAIR_MASSAGE, HOME_SERVICE, ADDITIONAL_SERVICES } from "@/data/extras";
import ReserveButton from "@/components/ReserveButton";
import { Button } from "@/components/ui/button";

// Menu Express — vue d'ensemble condensée. Tous les soins d'un coup d'œil,
// avec sonothérapie en clôture. Lien vers /menu pour la version détaillée.
export default function MenuSection() {
    return (
        <section
            id="menu"
            data-testid="menu-section"
            className="py-20 md:py-28 bg-white scroll-mt-24"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="max-w-2xl mb-10">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#E07A5F] mb-4">
                        Menu Express
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl font-light text-[#2C2A29] tracking-tight leading-tight">
                        Voici un aperçu rapide de la carte des{" "}
                        <span className="italic text-[#E07A5F]">soins proposés</span>
                        <br className="hidden sm:block" /> par Jérémy Tessier, massothérapeute.
                    </h2>
                    <p className="mt-4 text-[#6E6862] leading-relaxed text-base md:text-lg">
                        Massages en clinique, à domicile, en entreprise, événements privés — et une touche de sonothérapie qui traverse le corps comme une vibration de calme.
                    </p>
                    <div className="mt-6">
                        <ReserveButton />
                    </div>
                </div>

                {/* Massages en clinique — condensed rows */}
                <div className="rounded-3xl border border-[#E8E2D9] bg-[#FAF7F2] p-6 md:p-8 mb-4">
                    <h3 className="font-display text-xl text-[#2C2A29] mb-4">Massages en clinique</h3>
                    <div className="divide-y divide-[#E8E2D9]/70">
                        {SERVICES.map((s) => (
                            <div
                                key={s.id}
                                data-testid={`menu-svc-${s.id}`}
                                className="py-4 flex flex-wrap items-center justify-between gap-4"
                            >
                                <div className="min-w-0 flex-1">
                                    <div>
                                        <span className="font-display text-base text-[#2C2A29]">{s.name}</span>
                                        <span className="text-xs text-[#6E6862] ml-2">· {s.subtitle}</span>
                                    </div>
                                    <div className="text-sm text-[#2C2A29] font-medium mt-1">
                                        {s.variants.map((v, i) => (
                                            <span key={v.id}>
                                                {i > 0 && <span className="text-[#6E6862] mx-2">·</span>}
                                                {v.label} <span className="text-[#E07A5F]">{v.price} $</span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex flex-col items-stretch gap-1.5 shrink-0">
                                    <a
                                        href="/#reservation"
                                        data-testid={`menu-express-book-${s.id}`}
                                    >
                                        <Button
                                            size="sm"
                                            className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white text-xs px-4 py-2 h-auto whitespace-nowrap w-full"
                                        >
                                            Réserver <ArrowRight size={12} className="ml-1" />
                                        </Button>
                                    </a>
                                    <Link
                                        to={`/menu#svc-${s.id}`}
                                        data-testid={`menu-express-details-${s.id}`}
                                        className="text-[11px] text-[#3D8C95] hover:text-[#2C6D75] inline-flex items-center justify-center gap-1 whitespace-nowrap"
                                    >
                                        Plus de détails <ArrowRight size={11} />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Services supplémentaires — Yoga Thaï 100$ en clinique */}
                <div
                    data-testid="menu-additional-services"
                    className="rounded-3xl border-2 border-[#E07A5F]/25 bg-white p-6 md:p-8 mb-4"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Flower2 size={18} className="text-[#E07A5F]" />
                        <h3 className="font-display text-xl text-[#2C2A29]">Services supplémentaires</h3>
                    </div>
                    <div className="divide-y divide-[#E8E2D9]/70">
                        {ADDITIONAL_SERVICES.map((s) => (
                            <Link
                                to={`/${s.slug}`}
                                key={s.id}
                                data-testid={`menu-add-${s.id}`}
                                className="block py-3 -mx-2 px-2 rounded-xl hover:bg-[#FAF7F2] transition-colors"
                            >
                                <div className="flex flex-wrap items-baseline justify-between gap-3">
                                    <div className="min-w-0">
                                        <span className="font-display text-base text-[#2C2A29]">{s.name}</span>
                                        <span className="text-xs text-[#6E6862] ml-2">· {s.subtitle}</span>
                                    </div>
                                    <div className="text-sm text-[#2C2A29] font-medium whitespace-nowrap">
                                        {s.duration} <span className="text-[#E07A5F]">{s.price} $</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Forfaits — compact */}
                <div
                    data-testid="menu-express-packages"
                    className="rounded-3xl border border-[#E8E2D9] bg-white p-6 md:p-8 mb-4"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Package size={18} className="text-[#B7A46B]" />
                        <h3 className="font-display text-xl text-[#2C2A29]">Forfaits</h3>
                        <span className="text-xs text-[#6E6862] ml-1">· économisez jusqu&apos;à 160 $ · valides 12 mois</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {PACKAGES.map((p, i) => (
                            <div
                                key={p.id}
                                data-testid={`menu-express-pkg-${p.id}`}
                                className={`rounded-2xl p-4 border ${i === 2 ? "bg-[#2C2A29] text-white border-[#2C2A29]" : "bg-[#FAF7F2] border-[#E8E2D9]"}`}
                            >
                                <div className={`text-[10px] uppercase tracking-widest ${i === 2 ? "text-[#E07A5F]" : "text-[#3D8C95]"}`}>Forfait</div>
                                <div className="font-display text-2xl mt-1">{p.count} <span className="text-xs">massages</span></div>
                                <div className={`mt-2 pt-2 border-t ${i === 2 ? "border-white/20" : "border-[#E8E2D9]"} text-sm font-medium`}>
                                    {p.price} $ <span className={`text-xs ${i === 2 ? "text-[#E07A5F]" : "text-[#3D8C95]"}`}>· éco. {p.save} $</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Ajouts rapides */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <Link
                        to="/massage-domicile"
                        data-testid="menu-home-massage"
                        className="rounded-2xl p-5 bg-white border border-[#E8E2D9] hover:border-[#E07A5F] transition-colors"
                    >
                        <HomeIcon size={18} className="text-[#E07A5F] mb-2" />
                        <div className="font-display text-base text-[#2C2A29]">À domicile</div>
                        <div className="text-xs text-[#6E6862] mt-0.5">Tarif clinique + {HOME_SERVICE.surcharge} $</div>
                        <div className="text-xs text-[#3D8C95] mt-1">Rayon {HOME_SERVICE.radiusKm} km · forfait sans supplément</div>
                    </Link>
                    <Link
                        to="/massage-entreprise"
                        data-testid="menu-corporate"
                        className="rounded-2xl p-5 bg-white border border-[#E8E2D9] hover:border-[#3D8C95] transition-colors"
                    >
                        <Briefcase size={18} className="text-[#3D8C95] mb-2" />
                        <div className="font-display text-base text-[#2C2A29]">Entreprise</div>
                        <div className="text-xs text-[#6E6862] mt-0.5">Chair massage — {CHAIR_MASSAGE.hourlyRate} $ / h</div>
                        <div className="text-xs text-[#3D8C95] mt-1">15 · 30 · 45 · 60 min</div>
                    </Link>
                    <Link
                        to="/evenements"
                        data-testid="menu-events"
                        className="rounded-2xl p-5 bg-white border border-[#E8E2D9] hover:border-[#E07A5F] transition-colors"
                    >
                        <PartyPopper size={18} className="text-[#E07A5F] mb-2" />
                        <div className="font-display text-base text-[#2C2A29]">Événements</div>
                        <div className="text-xs text-[#6E6862] mt-0.5">Mariages, retraites — {CHAIR_MASSAGE.hourlyRate} $ / h</div>
                        <div className="text-xs text-[#3D8C95] mt-1">Clef en main</div>
                    </Link>
                    <Link
                        to="/cartes-cadeaux"
                        data-testid="menu-gift"
                        className="rounded-2xl p-5 bg-white border border-[#E8E2D9] hover:border-[#C44569] transition-colors"
                    >
                        <Gift size={18} className="text-[#C44569] mb-2" />
                        <div className="font-display text-base text-[#2C2A29]">Cartes-cadeaux</div>
                        <div className="text-xs text-[#6E6862] mt-0.5">Dès 80 $</div>
                        <div className="text-xs text-[#3D8C95] mt-1">Valides 12 mois</div>
                    </Link>
                </div>

                {/* Sonothérapie (dernière ligne comme demandé) */}
                <Link
                    to="/exercices#musicotherapie"
                    data-testid="menu-sonotherapy"
                    className="block rounded-3xl p-6 md:p-7 bg-gradient-to-br from-[#3D8C95] to-[#2C6D75] text-white hover:from-[#2C6D75] hover:to-[#3D8C95] transition-all"
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Music size={18} className="text-white/85" />
                                <span className="text-xs tracking-[0.2em] uppercase text-white/75">
                                    Musicothérapie · Sonothérapie
                                </span>
                            </div>
                            <h3 className="font-display text-2xl md:text-3xl">
                                Paysages sonores — kalimba, tongue drum, carillon
                            </h3>
                            <p className="mt-2 text-sm text-white/85 max-w-2xl leading-relaxed">
                                Intégrée au Massage Signature et au Massage Enrichi, ou en séance dédiée sur demande.
                                Vibrations profondes qui apaisent le système nerveux.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 text-sm font-medium text-white/95 whitespace-nowrap self-center">
                            Découvrir <ArrowRight size={16} />
                        </div>
                    </div>
                </Link>

                {/* Lien vers menu détaillé */}
                <div className="mt-8 text-center">
                    <Link to="/menu" data-testid="menu-full-link">
                        <Button
                            className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6"
                        >
                            Plus de détails — Menu complet <ArrowRight size={16} className="ml-2" />
                        </Button>
                    </Link>
                </div>
            </div>
        </section>
    );
}
